/**
 * MentionAutocomplete Component
 *
 * Dropdown autocomplete for @mentions in chat input.
 * Shows matching identities with their service availability, and (optionally) a
 * Workflows section so the user can switch the chat to a workflow straight from
 * the `@` popup. Each workflow row carries a `w1`, `w2` ... shortcut badge, and
 * typing `@w<n>` jumps straight to the nth workflow.
 *
 * A parallel Skills section mirrors Workflows: `@s` lists all skills, each row
 * carries an `s1`, `s2` ... shortcut badge, and typing `@s<n>` jumps to the nth
 * skill. Choosing a skill inserts an `@s_<id>` reference token into the input.
 *
 * Two further sections follow the identical pattern for the Workflow Builder's
 * chat composer (LSEG-91 Phase 6): Prompts behind `@p` and Connectors behind
 * `@c`. Both are purely additive — omitting the `prompts`/`connectors` props
 * leaves every other section byte-identical to before this pair was added.
 * `showIdentities` (default true) lets a caller suppress the people section
 * entirely, since the Workflow Builder has no use for mentioning a colleague.
 *
 * Workflow, skill, prompt and connector scopes are mutually exclusive; any
 * other query (a bare `@`, or `@se`/`@seb`) falls through to people search.
 */

import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { Avatar, Spinner, Link } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";

import { useMentions, useAddRecentIdentity } from "@/hooks/useMentions";
import { IdentitySearchResult } from "@/stores/MentionStore";

import { ServiceBadge, SUPPORTED_SERVICES } from "./ServiceBadge";

/** Minimal workflow shape needed to render a selectable `@` row. */
export interface MentionWorkflow {
  id: string;
  name: string;
  description?: string;
}

/** Minimal skill shape needed to render a selectable `@s` row. */
export interface MentionSkill {
  id: string;
  name: string;
  description?: string;
  icon?: string;
}

/** Minimal prompt shape needed to render a selectable `@p` row. */
export interface MentionPrompt {
  id: string;
  name: string;
  description?: string;
}

/** Minimal connector shape needed to render a selectable `@c` row. */
export interface MentionConnector {
  id: string;
  name: string;
  description?: string;
}

interface MentionAutocompleteProps {
  /** Search query (text after @) */
  query: string;
  /** Called when user selects an identity */
  onSelect: (identity: IdentitySearchResult) => void;
  /** Called when autocomplete should close (Escape, Tab, or selection). */
  onClose?: () => void;
  /** Position of the dropdown */
  position: { top: number; left: number };
  /** Whether the dropdown is visible */
  visible: boolean;

  // ── Workflows (optional; purely additive) ───────────────────────────────
  /** Workflows the user can switch to, surfaced above the people results. */
  workflows?: MentionWorkflow[];
  /** Id of the workflow currently active in the chat (marked with a tick). */
  activeWorkflowId?: string | null;
  /** Brand-resolved heading for the Workflows section. */
  workflowsLabel?: string;
  /** Called with a workflow id when a workflow row is chosen. */
  onSelectWorkflow?: (workflowId: string) => void;

  // ── Skills (optional; purely additive) ──────────────────────────────────
  /** Skills the user can reference, surfaced behind `@s` like workflows behind `@w`. */
  skills?: MentionSkill[];
  /** Heading for the Skills section. */
  skillsLabel?: string;
  /** Called with a skill id when a skill row is chosen. */
  onSelectSkill?: (skillId: string) => void;

  // ── Prompts (optional; purely additive — LSEG-91) ────────────────────────
  /** Saved prompts the user can reference, surfaced behind `@p`. */
  prompts?: MentionPrompt[];
  /** Heading for the Prompts section. */
  promptsLabel?: string;
  /** Called with a prompt id when a prompt row is chosen. */
  onSelectPrompt?: (promptId: string) => void;

  // ── Connectors (optional; purely additive — LSEG-91) ─────────────────────
  /** Connectors the user can reference, surfaced behind `@c`. */
  connectors?: MentionConnector[];
  /** Heading for the Connectors section. */
  connectorsLabel?: string;
  /** Called with a connector id when a connector row is chosen. */
  onSelectConnector?: (connectorId: string) => void;

  // ── People (optional; on by default) ─────────────────────────────────────
  /**
   * Whether to search and show people at all. Defaults to true so every
   * existing caller keeps its current behaviour untouched; the Workflow
   * Builder composer passes false since mentioning a colleague has no
   * meaning inside a workflow definition.
   */
  showIdentities?: boolean;
}

/** Matches the `@w<n>` quick-pick shortcut (e.g. "w1", "w12"). */
const WORKFLOW_SHORTCUT = /^w(\d+)$/;

/** Matches the `@s<n>` quick-pick shortcut (e.g. "s1", "s12"). */
const SKILL_SHORTCUT = /^s(\d+)$/;

/** Matches the `@p<n>` quick-pick shortcut (e.g. "p1", "p12"). */
const PROMPT_SHORTCUT = /^p(\d+)$/;

/** Matches the `@c<n>` quick-pick shortcut (e.g. "c1", "c12"). */
const CONNECTOR_SHORTCUT = /^c(\d+)$/;

/** Fallback glyph for a skill row when the catalogue entry has no icon. */
const DEFAULT_SKILL_ICON = "solar:magic-stick-3-linear";

/** Uniform glyph for every prompt row (per-prompt icons would be noisy). */
const PROMPT_ICON = "solar:document-text-linear";

/** Uniform glyph for every connector row (per-connector icons would be noisy). */
const CONNECTOR_ICON = "solar:plug-circle-linear";

/**
 * MentionAutocomplete displays a dropdown of matching identities (and,
 * optionally, workflows, skills, prompts or connectors) when the user types @
 * in the chat input.
 *
 * Features:
 * - Keyboard navigation (up/down arrows, enter to select, escape to close)
 * - Workflows section with `w1`..`wN` shortcut badges; `@w<n>` jumps to one
 * - Skills section with `s1`..`sN` shortcut badges; `@s<n>` jumps to one
 * - Prompts section with `p1`..`pN` shortcut badges; `@p<n>` jumps to one
 * - Connectors section with `c1`..`cN` shortcut badges; `@c<n>` jumps to one
 * - Shows service availability badges (Slack, Email, Teams, Google)
 * - Displays recent identities when query is empty
 * - Caches results for performance
 */
export const MentionAutocomplete: React.FC<MentionAutocompleteProps> = ({
  query,
  onSelect,
  onClose,
  position,
  visible,
  workflows,
  activeWorkflowId,
  workflowsLabel,
  onSelectWorkflow,
  skills,
  skillsLabel,
  onSelectSkill,
  prompts,
  promptsLabel,
  onSelectPrompt,
  connectors,
  connectorsLabel,
  onSelectConnector,
  showIdentities = true,
}) => {
  const { t } = useTranslation();
  const { results, loading, error, setQuery } = useMentions();
  const addRecentIdentity = useAddRecentIdentity();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // ── Workflow rows for the current query ────────────────────────────────
  // `wIndex` is the 1-based position in the FULL list, so the `w{n}` badge is
  // stable and `@w<n>` always resolves to the same workflow regardless of the
  // active text filter.
  const trimmedQuery = query.trim().toLowerCase();
  const hasWorkflows = (workflows?.length ?? 0) > 0;

  const hasSkills = (skills?.length ?? 0) > 0;
  const hasPrompts = (prompts?.length ?? 0) > 0;
  const hasConnectors = (connectors?.length ?? 0) > 0;

  // Workflows are scoped behind a leading "w" so the bare "@" stays free for
  // people, skills and anything else added later. "@w" lists all workflows,
  // "@w<n>" jumps to the nth. Any other query (including empty) is not workflows.
  const inWorkflowScope = hasWorkflows && /^w\d*$/.test(trimmedQuery);

  // Skills are scoped behind a leading "s", mirroring workflows. "@s" lists all
  // skills, "@s<n>" jumps to the nth. A query with letters after the "s" (e.g.
  // "@se", "@seb") is NOT a skill query — it falls through to people search so
  // existing name mentions keep working. Workflow and skill scopes are mutually
  // exclusive (the regexes can never both match the same query).
  const inSkillScope = hasSkills && /^s\d*$/.test(trimmedQuery);

  // Prompts and connectors mirror the same "leading letter, optional index"
  // scoping, behind "p" and "c" respectively.
  const inPromptScope = hasPrompts && /^p\d*$/.test(trimmedQuery);
  const inConnectorScope = hasConnectors && /^c\d*$/.test(trimmedQuery);

  const filteredWorkflows = useMemo(() => {
    if (!inWorkflowScope) return [];

    const all = (workflows ?? []).map((wf, i) => ({ wf, wIndex: i + 1 }));

    if (trimmedQuery === "w") return all;

    const shortcut = WORKFLOW_SHORTCUT.exec(trimmedQuery);

    if (shortcut) {
      const n = parseInt(shortcut[1], 10);

      return all.filter(({ wIndex }) => wIndex === n);
    }

    return all;
  }, [inWorkflowScope, workflows, trimmedQuery]);

  const filteredSkills = useMemo(() => {
    if (!inSkillScope) return [];

    // `sIndex` is the 1-based position in the FULL list so the `s{n}` badge is
    // stable and `@s<n>` always resolves to the same skill.
    const all = (skills ?? []).map((sk, i) => ({ sk, sIndex: i + 1 }));

    if (trimmedQuery === "s") return all;

    const shortcut = SKILL_SHORTCUT.exec(trimmedQuery);

    if (shortcut) {
      const n = parseInt(shortcut[1], 10);

      return all.filter(({ sIndex }) => sIndex === n);
    }

    return all;
  }, [inSkillScope, skills, trimmedQuery]);

  const filteredPrompts = useMemo(() => {
    if (!inPromptScope) return [];

    const all = (prompts ?? []).map((pr, i) => ({ pr, prIndex: i + 1 }));

    if (trimmedQuery === "p") return all;

    const shortcut = PROMPT_SHORTCUT.exec(trimmedQuery);

    if (shortcut) {
      const n = parseInt(shortcut[1], 10);

      return all.filter(({ prIndex }) => prIndex === n);
    }

    return all;
  }, [inPromptScope, prompts, trimmedQuery]);

  const filteredConnectors = useMemo(() => {
    if (!inConnectorScope) return [];

    const all = (connectors ?? []).map((co, i) => ({ co, coIndex: i + 1 }));

    if (trimmedQuery === "c") return all;

    const shortcut = CONNECTOR_SHORTCUT.exec(trimmedQuery);

    if (shortcut) {
      const n = parseInt(shortcut[1], 10);

      return all.filter(({ coIndex }) => coIndex === n);
    }

    return all;
  }, [inConnectorScope, connectors, trimmedQuery]);

  const workflowCount = filteredWorkflows.length;
  const skillCount = filteredSkills.length;
  const promptCount = filteredPrompts.length;
  const connectorCount = filteredConnectors.length;

  // While scoped to workflows/skills/prompts/connectors, the popup shows that
  // section only; otherwise it shows people (unless suppressed) plus hints.
  const showPeopleSection =
    showIdentities && !inWorkflowScope && !inSkillScope && !inPromptScope && !inConnectorScope;
  const totalRows =
    workflowCount +
    skillCount +
    promptCount +
    connectorCount +
    (showPeopleSection ? results.length : 0);

  // Update search query when prop changes
  useEffect(() => {
    setQuery(query);
    setSelectedIndex(0);
  }, [query, setQuery]);

  // Handle identity selection
  const handleSelect = useCallback(
    (identity: IdentitySearchResult) => {
      addRecentIdentity(identity);
      onSelect(identity);
    },
    [onSelect, addRecentIdentity],
  );

  // Keyboard navigation across the combined [workflows, skills, prompts, connectors, people] list
  useEffect(() => {
    if (!visible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedIndex((prev) => (totalRows === 0 ? 0 : (prev + 1) % totalRows));
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedIndex((prev) => (totalRows === 0 ? 0 : (prev - 1 + totalRows) % totalRows));
          break;
        case "Enter": {
          e.preventDefault();

          let cursor = 0;

          if (selectedIndex < cursor + workflowCount) {
            const picked = filteredWorkflows[selectedIndex - cursor];

            if (picked) onSelectWorkflow?.(picked.wf.id);
            break;
          }
          cursor += workflowCount;

          if (selectedIndex < cursor + skillCount) {
            const picked = filteredSkills[selectedIndex - cursor];

            if (picked) onSelectSkill?.(picked.sk.id);
            break;
          }
          cursor += skillCount;

          if (selectedIndex < cursor + promptCount) {
            const picked = filteredPrompts[selectedIndex - cursor];

            if (picked) onSelectPrompt?.(picked.pr.id);
            break;
          }
          cursor += promptCount;

          if (selectedIndex < cursor + connectorCount) {
            const picked = filteredConnectors[selectedIndex - cursor];

            if (picked) onSelectConnector?.(picked.co.id);
            break;
          }
          cursor += connectorCount;

          const identity = results[selectedIndex - cursor];

          if (identity) handleSelect(identity);
          break;
        }
        case "Escape":
          e.preventDefault();
          onClose?.();
          break;
        case "Tab":
          // Close on tab
          onClose?.();
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    visible,
    results,
    filteredWorkflows,
    filteredSkills,
    filteredPrompts,
    filteredConnectors,
    workflowCount,
    skillCount,
    promptCount,
    connectorCount,
    totalRows,
    selectedIndex,
    handleSelect,
    onSelectWorkflow,
    onSelectSkill,
    onSelectPrompt,
    onSelectConnector,
    onClose,
  ]);

  // Scroll selected item into view
  useEffect(() => {
    if (!containerRef.current) return;
    const selectedEl = containerRef.current.querySelector(`[data-index="${selectedIndex}"]`);

    if (selectedEl) {
      selectedEl.scrollIntoView({ block: "nearest" });
    }
  }, [selectedIndex]);

  if (!visible) return null;

  return (
    <div
      ref={containerRef}
      aria-label={t("Mention suggestions")}
      className="fixed z-50 bg-content1 border border-divider rounded-xl shadow-lg max-h-64 overflow-y-auto min-w-70 max-w-sm"
      role="listbox"
      style={{
        top: position.top,
        left: position.left,
        transform: "translateY(-100%)",
      }}
      tabIndex={-1}
      onMouseDown={(e) => e.preventDefault()}
    >
      {/* ── Workflows section (optional) ─────────────────────────────────── */}
      {workflowCount > 0 && (
        <>
          <div className="sticky top-0 z-10 flex items-stretch border-b border-divider bg-content1">
            <div className="flex flex-1 min-w-0 items-center px-3 py-2">
              <span className="text-tiny font-semibold uppercase tracking-wider text-default-400">
                {workflowsLabel || t("chatCommandMenu.sections.workflows")}
              </span>
            </div>
            {/* Close (X) — pointer/touch dismissal (Esc is unavailable on mobile). */}
            <button
              aria-label={t("Close")}
              className="flex shrink-0 items-center justify-center px-3 min-h-11 text-default-400 hover:text-default-600 hover:bg-default-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset"
              data-id="mention-autocomplete-workflows-close"
              type="button"
              onClick={() => onClose?.()}
            >
              <Icon className="w-4 h-4" icon="solar:close-circle-linear" />
            </button>
          </div>

          {filteredWorkflows.map(({ wf, wIndex }, i) => (
            <div
              key={wf.id}
              aria-selected={i === selectedIndex}
              className={`
                px-3 py-2 cursor-pointer flex items-center gap-3
                hover:bg-default-100 transition-colors
                ${i === selectedIndex ? "bg-default-100" : ""}
              `}
              data-index={i}
              role="option"
              tabIndex={0}
              onClick={() => onSelectWorkflow?.(wf.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSelectWorkflow?.(wf.id);
                }
              }}
              onMouseEnter={() => setSelectedIndex(i)}
            >
              {/* Shortcut badge — type @w{wIndex} to jump straight here */}
              <span
                aria-hidden="true"
                className="shrink-0 px-1.5 py-0.5 rounded bg-default-100 text-[10px] font-mono font-semibold text-default-500 min-w-[26px] text-center"
              >
                w{wIndex}
              </span>

              {/* Workflow glyph (uniform; per-workflow icons are noisy) */}
              <Icon className="w-4 h-4 text-default-500 shrink-0" icon="solar:widget-2-linear" />

              {/* Name + description */}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{wf.name}</div>
                {wf.description && (
                  <div className="text-xs text-default-500 truncate">{wf.description}</div>
                )}
              </div>

              {/* Active tick, otherwise a chevron */}
              {wf.id === activeWorkflowId ? (
                <Icon
                  aria-label={t("chatCommandMenu.workflows.active")}
                  className="w-4 h-4 text-success-500 shrink-0"
                  icon="solar:check-circle-bold"
                />
              ) : (
                <Icon
                  className="w-3 h-3 text-default-300 shrink-0"
                  icon="solar:alt-arrow-right-linear"
                />
              )}
            </div>
          ))}

          {(skillCount > 0 || promptCount > 0 || connectorCount > 0 || showPeopleSection) && (
            <div className="h-px bg-divider" />
          )}
        </>
      )}

      {/* ── Skills section (optional) ─────────────────────────────────────── */}
      {skillCount > 0 && (
        <>
          <div className="sticky top-0 z-10 flex items-stretch border-b border-divider bg-content1">
            <div className="flex flex-1 min-w-0 items-center px-3 py-2">
              <span className="text-tiny font-semibold uppercase tracking-wider text-default-400">
                {skillsLabel || t("chatCommandMenu.sections.skills")}
              </span>
            </div>
            {/* Close (X) — pointer/touch dismissal (Esc is unavailable on mobile). */}
            <button
              aria-label={t("Close")}
              className="flex shrink-0 items-center justify-center px-3 min-h-11 text-default-400 hover:text-default-600 hover:bg-default-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset"
              data-id="mention-autocomplete-skills-close"
              type="button"
              onClick={() => onClose?.()}
            >
              <Icon className="w-4 h-4" icon="solar:close-circle-linear" />
            </button>
          </div>

          {filteredSkills.map(({ sk, sIndex }, i) => {
            const rowIndex = workflowCount + i;

            return (
              <div
                key={sk.id}
                aria-selected={rowIndex === selectedIndex}
                className={`
                  px-3 py-2 cursor-pointer flex items-center gap-3
                  hover:bg-default-100 transition-colors
                  ${rowIndex === selectedIndex ? "bg-default-100" : ""}
                `}
                data-index={rowIndex}
                role="option"
                tabIndex={0}
                onClick={() => onSelectSkill?.(sk.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSelectSkill?.(sk.id);
                  }
                }}
                onMouseEnter={() => setSelectedIndex(rowIndex)}
              >
                {/* Shortcut badge — type @s{sIndex} to jump straight here */}
                <span
                  aria-hidden="true"
                  className="shrink-0 px-1.5 py-0.5 rounded bg-default-100 text-[10px] font-mono font-semibold text-default-500 min-w-[26px] text-center"
                >
                  s{sIndex}
                </span>

                {/* Skill glyph (per-skill icon, with a uniform fallback) */}
                <Icon
                  className="w-4 h-4 text-default-500 shrink-0"
                  icon={sk.icon || DEFAULT_SKILL_ICON}
                />

                {/* Name + description */}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{sk.name}</div>
                  {sk.description && (
                    <div className="text-xs text-default-500 truncate">{sk.description}</div>
                  )}
                </div>

                <Icon
                  className="w-3 h-3 text-default-300 shrink-0"
                  icon="solar:alt-arrow-right-linear"
                />
              </div>
            );
          })}

          {(promptCount > 0 || connectorCount > 0 || showPeopleSection) && (
            <div className="h-px bg-divider" />
          )}
        </>
      )}

      {/* ── Prompts section (optional — LSEG-91) ─────────────────────────── */}
      {promptCount > 0 && (
        <>
          <div className="sticky top-0 z-10 flex items-stretch border-b border-divider bg-content1">
            <div className="flex flex-1 min-w-0 items-center px-3 py-2">
              <span className="text-tiny font-semibold uppercase tracking-wider text-default-400">
                {promptsLabel || t("chatCommandMenu.sections.prompts")}
              </span>
            </div>
            <button
              aria-label={t("Close")}
              className="flex shrink-0 items-center justify-center px-3 min-h-11 text-default-400 hover:text-default-600 hover:bg-default-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset"
              data-id="mention-autocomplete-prompts-close"
              type="button"
              onClick={() => onClose?.()}
            >
              <Icon className="w-4 h-4" icon="solar:close-circle-linear" />
            </button>
          </div>

          {filteredPrompts.map(({ pr, prIndex }, i) => {
            const rowIndex = workflowCount + skillCount + i;

            return (
              <div
                key={pr.id}
                aria-selected={rowIndex === selectedIndex}
                className={`
                  px-3 py-2 cursor-pointer flex items-center gap-3
                  hover:bg-default-100 transition-colors
                  ${rowIndex === selectedIndex ? "bg-default-100" : ""}
                `}
                data-index={rowIndex}
                role="option"
                tabIndex={0}
                onClick={() => onSelectPrompt?.(pr.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSelectPrompt?.(pr.id);
                  }
                }}
                onMouseEnter={() => setSelectedIndex(rowIndex)}
              >
                <span
                  aria-hidden="true"
                  className="shrink-0 px-1.5 py-0.5 rounded bg-default-100 text-[10px] font-mono font-semibold text-default-500 min-w-[26px] text-center"
                >
                  p{prIndex}
                </span>

                <Icon className="w-4 h-4 text-default-500 shrink-0" icon={PROMPT_ICON} />

                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{pr.name}</div>
                  {pr.description && (
                    <div className="text-xs text-default-500 truncate">{pr.description}</div>
                  )}
                </div>

                <Icon
                  className="w-3 h-3 text-default-300 shrink-0"
                  icon="solar:alt-arrow-right-linear"
                />
              </div>
            );
          })}

          {(connectorCount > 0 || showPeopleSection) && <div className="h-px bg-divider" />}
        </>
      )}

      {/* ── Connectors section (optional — LSEG-91) ──────────────────────── */}
      {connectorCount > 0 && (
        <>
          <div className="sticky top-0 z-10 flex items-stretch border-b border-divider bg-content1">
            <div className="flex flex-1 min-w-0 items-center px-3 py-2">
              <span className="text-tiny font-semibold uppercase tracking-wider text-default-400">
                {connectorsLabel || t("workflowBuilder.mentions.connectors")}
              </span>
            </div>
            <button
              aria-label={t("Close")}
              className="flex shrink-0 items-center justify-center px-3 min-h-11 text-default-400 hover:text-default-600 hover:bg-default-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset"
              data-id="mention-autocomplete-connectors-close"
              type="button"
              onClick={() => onClose?.()}
            >
              <Icon className="w-4 h-4" icon="solar:close-circle-linear" />
            </button>
          </div>

          {filteredConnectors.map(({ co, coIndex }, i) => {
            const rowIndex = workflowCount + skillCount + promptCount + i;

            return (
              <div
                key={co.id}
                aria-selected={rowIndex === selectedIndex}
                className={`
                  px-3 py-2 cursor-pointer flex items-center gap-3
                  hover:bg-default-100 transition-colors
                  ${rowIndex === selectedIndex ? "bg-default-100" : ""}
                `}
                data-index={rowIndex}
                role="option"
                tabIndex={0}
                onClick={() => onSelectConnector?.(co.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSelectConnector?.(co.id);
                  }
                }}
                onMouseEnter={() => setSelectedIndex(rowIndex)}
              >
                <span
                  aria-hidden="true"
                  className="shrink-0 px-1.5 py-0.5 rounded bg-default-100 text-[10px] font-mono font-semibold text-default-500 min-w-[26px] text-center"
                >
                  c{coIndex}
                </span>

                <Icon className="w-4 h-4 text-default-500 shrink-0" icon={CONNECTOR_ICON} />

                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{co.name}</div>
                  {co.description && (
                    <div className="text-xs text-default-500 truncate">{co.description}</div>
                  )}
                </div>

                <Icon
                  className="w-3 h-3 text-default-300 shrink-0"
                  icon="solar:alt-arrow-right-linear"
                />
              </div>
            );
          })}

          {showPeopleSection && <div className="h-px bg-divider" />}
        </>
      )}

      {/* ── People section ───────────────────────────────────────────────── */}
      {showPeopleSection && (
        <>
          {/* Header */}
          <div className="sticky top-0 z-10 flex items-stretch border-b border-divider bg-content1">
            <div className="flex flex-1 min-w-0 items-center px-3 py-2">
              <span className="text-xs text-default-500">
                {query.length === 0 ? t("Recent mentions") : t("Search results")}
              </span>
            </div>
            {/* Close (X) — pointer/touch dismissal (Esc is unavailable on mobile). */}
            <button
              aria-label={t("Close")}
              className="flex shrink-0 items-center justify-center px-3 min-h-11 text-default-400 hover:text-default-600 hover:bg-default-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset"
              data-id="mention-autocomplete-people-close"
              type="button"
              onClick={() => onClose?.()}
            >
              <Icon className="w-4 h-4" icon="solar:close-circle-linear" />
            </button>
          </div>

          {/* Discoverability hint: workflows live behind `@w` */}
          {query.length === 0 && hasWorkflows && (
            <div className="px-3 py-2 text-tiny text-default-400 border-b border-divider flex items-center gap-1.5">
              <span
                aria-hidden="true"
                className="px-1.5 py-0.5 rounded bg-default-100 text-[10px] font-mono font-semibold text-default-500"
              >
                w
              </span>
              {t("mentionAutocomplete.workflowsHint")}
            </div>
          )}

          {/* Discoverability hint: skills live behind `@s` */}
          {query.length === 0 && hasSkills && (
            <div className="px-3 py-2 text-tiny text-default-400 border-b border-divider flex items-center gap-1.5">
              <span
                aria-hidden="true"
                className="px-1.5 py-0.5 rounded bg-default-100 text-[10px] font-mono font-semibold text-default-500"
              >
                s
              </span>
              {t("mentionAutocomplete.skillsHint")}
            </div>
          )}

          {/* Loading state */}
          {loading && (
            <div aria-live="polite" className="flex items-center justify-center py-4" role="status">
              <Spinner aria-label={t("Loading...")} size="sm" />
            </div>
          )}

          {/* Error state */}
          {error && <div className="px-3 py-4 text-sm text-danger">{error}</div>}

          {/* Empty state */}
          {!loading && !error && results.length === 0 && (
            <div className="px-3 py-4 text-sm text-default-500 text-center">
              {query.length > 0 ? t("No matches found") : t("No recent mentions")}
            </div>
          )}

          {/* Results list */}
          {!loading &&
            !error &&
            results.map((identity, index) => {
              const rowIndex = workflowCount + skillCount + promptCount + connectorCount + index;

              return (
                <div
                  key={identity.id}
                  aria-selected={rowIndex === selectedIndex}
                  className={`
                    px-3 py-2 cursor-pointer flex items-center gap-3
                    hover:bg-default-100 transition-colors
                    ${rowIndex === selectedIndex ? "bg-default-100" : ""}
                  `}
                  data-index={rowIndex}
                  role="option"
                  tabIndex={0}
                  onClick={() => handleSelect(identity)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleSelect(identity);
                    }
                  }}
                  onMouseEnter={() => setSelectedIndex(rowIndex)}
                >
                  {/* Avatar */}
                  <Avatar
                    className="flex-shrink-0"
                    name={identity.displayName}
                    size="sm"
                    src={identity.avatarUrl}
                  />

                  {/* Name and email */}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{identity.displayName}</div>
                    <div className="text-xs text-default-500 truncate">
                      {identity.canonicalEmail}
                    </div>
                  </div>

                  {/* Service badges */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {SUPPORTED_SERVICES.map((service) => (
                      <ServiceBadge
                        key={service}
                        available={identity.availableServices.includes(service)}
                        service={service}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
        </>
      )}

      {/* Footer hint — pinned so it stays visible while the list scrolls */}
      <div className="sticky bottom-0 z-10 bg-content1 px-3 py-2 border-t border-divider text-xs text-default-400 flex items-center justify-between">
        <div>
          <span className="mr-4">
            <kbd className="px-1 py-0.5 bg-default-100 rounded text-[10px]">↑↓</kbd> {t("navigate")}
          </span>
          <span className="mr-4">
            <kbd className="px-1 py-0.5 bg-default-100 rounded text-[10px]">Enter</kbd>{" "}
            {t("select")}
          </span>
          <span>
            <kbd className="px-1 py-0.5 bg-default-100 rounded text-[10px]">Esc</kbd> {t("close")}
          </span>
        </div>
        <Link
          className="text-xs text-primary hover:underline flex items-center gap-1"
          href="/settings#mentions"
          size="sm"
        >
          <Icon className="w-3 h-3" icon="solar:settings-linear" />
          {t("Add services")}
        </Link>
      </div>
    </div>
  );
};

export default MentionAutocomplete;
